// bluaIQ — Excel export button (owner-only). Server-side gate is the real
// boundary; this is the surface that reaches it.
//
// WHERE IT LIVES, and why not where you'd expect: the obvious home was
// TakeoffRealQuantifyFlow, but that component unmounts as soon as a payload
// exists (`showRealFlowState = isRealSelected && !payload`) — precisely when an
// export becomes useful. So the button renders in the PAYLOAD branch instead,
// gated on a REAL project-scoped payload.
//
// CONDITION 7, standing: there is no Compra/Purchase sheet and no price anywhere
// in this flow. The copy below must never imply one exists.

import { useEffect, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { Download } from 'lucide-react'

import {
  downloadTakeoffExcel,
  fetchTakeoffExcelExportAvailability,
  EXPORT_ERR,
} from './takeoffQuantifyClient'

type Bi = { en: string; es: string }

/** Every string the owner can see. ES uses «usted», no engine jargon, and
 *  NOTHING here promises a final or purchase-ready quantity — the document is
 *  candidate quantities pending review, and the button must say so before it is
 *  pressed, not only inside the file. */
const COPY: Record<string, Bi> = {
  action: { en: 'Download Excel', es: 'Descargar Excel' },
  working: { en: 'Preparing the file…', es: 'Preparando el archivo…' },
  // The honesty line. It sits WITH the button, because a download is the moment
  // a number stops being on screen and starts being a file someone forwards.
  caveat: {
    en: 'Candidate quantities, pending review — not final and not ready for purchase.',
    es: 'Cantidades candidatas, pendientes de revisión — no son finales ni están listas para compra.',
  },
  done: { en: 'File downloaded.', es: 'Archivo descargado.' },

  // One message per status. Each says what happened AND what to do — an error
  // the owner cannot act on is just an apology.
  [EXPORT_ERR.session]: {
    en: 'Your session expired. Sign in again and retry the download.',
    es: 'Su sesión expiró. Vuelva a iniciar sesión e intente la descarga de nuevo.',
  },
  [EXPORT_ERR.notOwner]: {
    en: 'This export is only available to the account that owns the project.',
    es: 'Esta exportación solo está disponible para la cuenta dueña del proyecto.',
  },
  [EXPORT_ERR.noPayload]: {
    en: 'There is no reading saved for this project yet. Run a reading first.',
    es: 'Todavía no hay una lectura guardada para este proyecto. Haga una lectura primero.',
  },
  [EXPORT_ERR.contract]: {
    en: 'The saved reading did not pass its safety check, so no file was produced. Run the reading again.',
    es: 'La lectura guardada no pasó su verificación de seguridad, así que no se generó ningún archivo. Repita la lectura.',
  },
  [EXPORT_ERR.unknown]: {
    en: 'The file could not be prepared. Please try again.',
    es: 'No se pudo preparar el archivo. Intente de nuevo.',
  },
}

export default function TakeoffExcelExportButton({
  projectId,
  isEs,
}: {
  projectId: string
  isEs: boolean
}) {
  const { getToken } = useAuth()
  const [busy, setBusy] = useState(false)
  const [errKey, setErrKey] = useState<string | null>(null)
  const [ok, setOk] = useState(false)

  // UI EXPOSURE FLAG. Starts `false` and only ever becomes true on an explicit
  // server `enabled: true`, so the button is absent during the in-flight window
  // too — it never flashes on and then disappears. Every failure path in
  // fetchTakeoffExcelExportAvailability already returns false (fail-closed).
  //
  // This hides the button; it does not protect the endpoint. export.xlsx is
  // owner-gated server-side and does not read this flag (condition 3).
  const [exposed, setExposed] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const allowed = await fetchTakeoffExcelExportAvailability(projectId, await getToken())
      if (!cancelled) setExposed(allowed)
    })()
    return () => { cancelled = true }
  }, [projectId, getToken])

  const L = (k: Bi) => (isEs ? k.es : k.en)
  const lang: 'es' | 'en' = isEs ? 'es' : 'en'

  async function onDownload() {
    setBusy(true); setErrKey(null); setOk(false)
    try {
      // GET with the ACTIVE language and a fresh token. No body: the client
      // never hands the server a payload to render (endpoint condition 4).
      const { blob, filename } = await downloadTakeoffExcel(projectId, lang, await getToken())
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      setOk(true)
    } catch (e) {
      // Map to a KNOWN key only. An unrecognised error must not render its raw
      // message: engine text and stack traces are exactly the slugs conditions 6
      // forbids from reaching any view.
      const raw = e instanceof Error ? e.message : ''
      setErrKey(Object.values(EXPORT_ERR).includes(raw as never) ? raw : EXPORT_ERR.unknown)
    } finally {
      setBusy(false)
    }
  }

  // FLAG OFF ⇒ render NOTHING. Not a disabled button, not a tooltip, not an
  // empty bordered card — an account outside the allowlist must see no trace
  // that an export exists (condition 4). Placed after the hooks so hook order
  // stays stable across the enabled/disabled transition.
  if (!exposed) return null

  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
      {/* Mobile 390: the button is full-width and stacks above the caveat; from
          sm: they sit side by side. min-h-[44px] keeps the touch target legal. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={onDownload}
          disabled={busy}
          className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md
                     bg-[#00BCD4] px-4 py-2 text-sm font-medium text-white
                     hover:bg-[#00a5bb] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          <Download size={16} aria-hidden />
          {busy ? L(COPY.working) : L(COPY.action)}
        </button>

        <p className="text-xs leading-snug text-slate-600 sm:max-w-xs sm:text-right">
          {L(COPY.caveat)}
        </p>
      </div>

      {ok && !errKey && (
        <p className="mt-3 text-xs text-emerald-700">{L(COPY.done)}</p>
      )}
      {errKey && (
        <p role="alert" className="mt-3 text-xs text-rose-700">
          {L(COPY[errKey] ?? COPY[EXPORT_ERR.unknown])}
        </p>
      )}
    </div>
  )
}
